import withStore, {
  TPresentable,
  TLoadingAwarePresenter
} from "../../core/presenter/withStore";
import Application from "../../Application";
import CreateAttachmentFileInteractor from "../../caseLawDocuments/interactor/attachments/CreateAttachmentFileInteractor";
import CreateAttachmentInteractor from "../../caseLawDocuments/interactor/attachments/CreateAttachmentInteractor";
import ShowSuccessMessageInteractor from "../../core/interactor/notifications/ShowSuccessMessageInteractor";
import AttachmentPresentationModel, {
  TAttachmentPresentationModel
} from "../../caseLawDocuments/presenter/model/AttachmentPresentationModel";
import { IOrganisationIndex } from "../../organisations/model/OrganisationIndex";
import AttachmentTypePresentationModel, {
  TAttachmentTypePresentationModel
} from "../../organisations/presenter/model/AttachmentTypePresentationModel";

import { IOrganisation } from "../../organisations/model/Organisation";
import { IGenericCodebookItem } from "../../codebooks/model/GenericCodebookItem";
import { IAttachmentType } from "../../attachmentType/model/AttachmentType";
import CaseDecisionPresentationModel, {
  TCaseDecisionPresentationModel
} from "./model/CaseDecisionPresentationModel";
import { ICaseDecision } from "../model/CaseDecision";
import { IUser } from "../../user/model/User";
import { IAttachment } from "../../caseLawDocuments/model/Attachment";
import GetAttachmentsForCaseDecisionInteractor from "../interactor/GetAttachmentsForCaseDecisionInteractor";
import RemoveCaseDecisionAttachmentInteractor from "../interactor/RemoveCaseDecisionAttachmentInteractor";
import GetAllOrganisationAttachmentTypesInteractor from "../../organisations/interactors/GetAllOrganisationAttachmentTypesInteractor";
import { ContentTypes } from "../../caseLawDocuments/common/Constants";
import { ICourtCase } from "../../caseLawDocuments/model/CourtCase";
import OrganisationIndexPresentationModel, {
  TOrganisationIndexPresentationModel
} from "../../organisations/presenter/model/OrganisationIndexPresentationModel";
import {
  IDocumentType,
  TDocumentType
} from "../../documentTypes/model/DocumentType";
import UpdateCaseDecisionInteractor from "../interactor/UpdateCaseDecisionInteractor";
import { TOrganisationIndexGroupPresentationModel } from "../../organisations/presenter/model/OrganisationIndexGroupPresentationModel";
import PublishCaseDecisionInteractor from "../interactor/PublishCaseDecisionInteractor";
import ArchiveCaseDecisionInteractor from "../interactor/ArchiveCaseDecisionInteractor";
import { IDocumentTypeIndexCategory } from "../../documentTypes/model/DocumentTypeIndexCategory";
import DocumentTypeIndexCategoryPresentationModel from "../../documentTypes/presenter/DocumentTypeIndexCategoryPresentationModel";
import FindDecisionIndexesInCategoryInteractor from "../interactor/FindDecisionIndexesInCategoryInteractor";
import { IInvalidatedCaseDecision } from "../model/InvalidatedCaseDecision";
import GetAllIndexTypeAttributesInteractor from "../../indexTypes/interactors/GetAllIndexTypeAttributesInteractor";
import { IIndexTypeAttribute } from "../../indexTypes/model/IndexTypeAttribute";
import IndexAttributeData, {
  IIndexAttributeData
} from "../../indexTypes/model/IndexAttributeData";
import CreateIndexAttributeDataInteractor from "../../indexTypes/interactors/CreateIndexAttributeDataInteractor";
import GetIndexAttributeDataByIndexInteractor from "../../indexTypes/interactors/GetIndexAttributeDataByIndexInteractor";
import ShowWarningMessageInteractor from "../../core/interactor/notifications/ShowWarningMessageInteractor";
import { Constants } from "../../core/common/Constants";

export interface TCaseDecisionUpdatePresenter extends TLoadingAwarePresenter {
  caseDecision: TCaseDecisionPresentationModel;
  organisations: IOrganisation[];
  caseDecisionTypes: TDocumentType[];
  caseDecisionIndexes: TOrganisationIndexPresentationModel[];
  attachmentTypes: TAttachmentTypePresentationModel[];
  attachmentType: any;
  contentTypes: IGenericCodebookItem[];
  attachmentList: TAttachmentPresentationModel[];
  mainAttachment: TAttachmentPresentationModel;
  selectedParentGroupIdList: number[];
  selectedSecondGroupIdList: number[];
  parentGroupList: TOrganisationIndexGroupPresentationModel[][];
  secondGroupList: TOrganisationIndexGroupPresentationModel[][];
  invalidatedCaseDecisions: TCaseDecisionPresentationModel[];
  organisationIndexes: IDocumentTypeIndexCategory[];
  publishedCaseDecisions: any[];
  invalidatePublishedCaseDecisionsVisible: boolean;
  uploadedFile: any;
  indexHierarchyList: string[][];
  user?: IUser;
  deletedAttachments: IAttachment[];
  courtCase: ICourtCase;
  attachmentModalVisible: boolean;
  anonimisedUploadedFile: any;
  selectedIndexes: TOrganisationIndexPresentationModel[][];
  mainAttachmentType?: any;
  selectedIndexForAttribute?: IOrganisationIndex;
  indexTypeAttributes?: IIndexTypeAttribute[];
  indexAttributeDataList?: IIndexAttributeData[];
  indexDataModalVisible: boolean;
  previousSelectedIds?: number[];
  decisionTypes: IGenericCodebookItem[];
}

export interface ICaseDecisionUpdatePresenter
  extends TCaseDecisionUpdatePresenter,
    TPresentable {
  loadCaseDecision(caseDecisions: ICaseDecision): void;
  loadReferenceValues(
    caseDecisionTypes: IDocumentType[],
    organisations: IOrganisation[],
    attachmentTypes: IAttachmentType[],
    contentTypes: IGenericCodebookItem[],
    courtCase: ICourtCase,
    invalidatedCaseDecisions: ICaseDecision[]
  ): void;
  loadAttachments(attachmentList: IAttachment[]): void;
  loadCaseDecisionIndexes(caseDecisionIndexes: IOrganisationIndex[]);
  createAttachment(attachment: IAttachment): void;
  setFileValue(file): void;
  updateAttachment(attachment: any, main?: boolean): void;
  updateMainAttachment(attachment: any): Promise<void>;
  showCaseDecisionDetailsPage(id: string): void;
  getAttachments(caseDecision: ICaseDecision): void;
  setAttachmentType(attachmentType: string): void;
  deleteAttachment(attachment: TAttachmentPresentationModel): void;
  onUpdate(caseDecision: ICaseDecision): void;
  updateCaseDecision(item: any): void;
  canEdit(organisationId): void;
  toggleAttachementModal(visible: boolean): void;
  setAnonymisedFileValue(file: any): void;
  publishCaseDecision(): void;
  archiveCaseDecision(): void;
  setParentGroupId(selectedSecondGroupId: number, index: number): void;
  setSecondGroupId(selectedSecondGroupId: number, index: number): void;
  setCaseDecisionTypeCode(caseDecisionTypeId: number): void;
  setMainAttachmentType(mainAttachmentType: any): void;
  openIndexDataModal(index: IOrganisationIndex, selectedIds: number[]): void;
  closeIndexDataModal(): void;
  setAttributesData(
    indexTypeAttribute: IIndexTypeAttribute,
    attributeData: string
  ): void;
  saveAttributeData(): void;
}

const defaultState: TCaseDecisionUpdatePresenter = {
  caseDecision: {} as TCaseDecisionPresentationModel,
  organisations: [],
  caseDecisionTypes: [],
  caseDecisionIndexes: [],
  attachmentTypes: [],
  contentTypes: [],
  attachmentList: [],
  uploadedFile: undefined,
  mainAttachment: {} as TAttachmentPresentationModel,
  indexHierarchyList: [],
  attachmentType: {},
  deletedAttachments: [],
  attachmentModalVisible: false,
  courtCase: {} as ICourtCase,
  anonimisedUploadedFile: undefined,
  selectedParentGroupIdList: [],
  selectedSecondGroupIdList: [],
  parentGroupList: [],
  secondGroupList: [],
  invalidatedCaseDecisions: [],
  publishedCaseDecisions: [],
  invalidatePublishedCaseDecisionsVisible: false,
  organisationIndexes: [],
  selectedIndexes: [],
  mainAttachmentType: {},
  decisionTypes: [],
  indexAttributeDataList: [],
  indexDataModalVisible: false
};

const CaseDecisionUpdatePresenter = withStore<
  ICaseDecisionUpdatePresenter,
  TCaseDecisionUpdatePresenter
>(({ application, store, loader, translate }): ICaseDecisionUpdatePresenter => {
  const _store = store;
  const _application: Application = application;
  const state = _store.getState<
    TCaseDecisionUpdatePresenter & TLoadingAwarePresenter
  >();

  const loadCaseDecision = (item: ICaseDecision) => {
    const { organisations, caseDecisionTypes, attachmentTypes } =
      _store.getState<TCaseDecisionUpdatePresenter>();

    const caseDecisionType = caseDecisionTypes?.find(
      (value) => value.id === item.documentTypeId
    );
    const organisation = organisations?.find(
      (value) => value.id === item.organisationId
    );
    let attachment = item.attachment;
    let attachmentType = attachment
      ? attachmentTypes.find((type) => type.id === attachment?.attachmentTypeId)
      : null;
    const mainAttachment =
      attachment && attachmentType
        ? AttachmentPresentationModel(attachment, attachmentType)
        : {};
    const caseDecision = CaseDecisionPresentationModel(
      item,
      caseDecisionType,
      organisation
    );
    _store.update({
      caseDecision,
      mainAttachment
    });
    loader.stop("caseDecisionLoader");
  };
  const loadReferenceValues = (
    caseDecisionTypes: IDocumentType[],
    organisations: IOrganisation[],
    attachmentTypesList: IAttachmentType[],
    contentTypes: IGenericCodebookItem[],
    courtCase: ICourtCase,
    invalidatedCaseDecisionList: ICaseDecision[]
  ) => {
    let attachmentTypes = (attachmentTypesList || []).map((item) => {
      const contentType = contentTypes?.find(
        (value) => value.id === item.attachmentContentTypeId
      );
      return AttachmentTypePresentationModel(item, contentType);
    });
    const user = _store.getState<any>().user;

    if (caseDecisionTypes.length > 0) {
      setCaseDecisionTypeCode(caseDecisionTypes[0].id);
    }

    const invalidatedCaseDecisions = invalidatedCaseDecisionList.map((item) => {
      const caseDecisionType = caseDecisionTypes?.find(
        (value) => value.id === item.documentTypeId
      );
      const organisation = organisations?.find(
        (value) => value.id === item.organisationId
      );
      return CaseDecisionPresentationModel(
        item,
        caseDecisionType,
        organisation
      );
    });

    _store.update({
      caseDecisionTypes,
      organisations,
      contentTypes,
      attachmentTypes,
      user,
      courtCase,
      invalidatedCaseDecisions
    });
  };

  const getCaseDecisionTypeIndexes = async (caseDecisionTypeCode: number) => {
    let organisationIndexes = await _application.container
      .resolve<FindDecisionIndexesInCategoryInteractor>(
        "findDecisionIndexesInCategory"
      )
      .execute(caseDecisionTypeCode, 1);
    loadCaseDecisionTypeIndexes(organisationIndexes);
  };

  const loadCaseDecisionTypeIndexes = (
    organisationIndexes: IDocumentTypeIndexCategory[]
  ) => {
    const { selectedLanguage, caseDecisionIndexes } = _store.getState<any>();
    var selectedIndexByCategories: IOrganisationIndex[][] = [];
    organisationIndexes.forEach((item) => {
      const result = caseDecisionIndexes.filter((o) =>
        item.organisationIndexList?.some(({ id }) => o.id === id)
      );
      selectedIndexByCategories.push(result);
    });
    _store.update({
      organisationIndexes: organisationIndexes.map((item) =>
        DocumentTypeIndexCategoryPresentationModel(item, selectedLanguage)
      ),
      selectedIndex: selectedIndexByCategories
    });
    var selectedIndexes = selectedIndexByCategories;
    let selectedParentGroupIdList: number[] = [];
    selectedParentGroupIdList.length = selectedIndexes.length;
    selectedIndexes.forEach((selectedIndex, index) => {
      selectedIndex.forEach((element) => {
        const selectedParentGroupId = element?.groups.find(
          (group) => group.parentId == null
        )?.id;
        selectedParentGroupIdList[index] = selectedParentGroupId ?? NaN;
      });
    });

    let selectedSecondGroupIdList: number[] = [];
    selectedSecondGroupIdList.length = selectedIndexes.length;
    selectedIndexes.forEach((selectedIndex, index) => {
      selectedIndex.forEach((element) => {
        const selectedSecondGroupId = element?.groups.find(
          (group) => group.parentId === selectedParentGroupIdList[index]
        )?.id;
        selectedSecondGroupIdList[index] = selectedSecondGroupId ?? NaN;
      });
    });

    let parentGroupList: TOrganisationIndexGroupPresentationModel[][] = [];
    let secondGroupList: TOrganisationIndexGroupPresentationModel[][] = [];
    organisationIndexes.forEach((item) => {
      let parentList: TOrganisationIndexGroupPresentationModel[] = [];
      let secondList: TOrganisationIndexGroupPresentationModel[] = [];
      let indexList = item.organisationIndexList?.map((element) =>
        OrganisationIndexPresentationModel(
          element,
          undefined,
          undefined,
          undefined,
          undefined,
          selectedLanguage
        )
      );
      indexList?.forEach((index) => {
        var parentGroup = index.groups.find((group) => group.parentId == null);
        var secondGroup = index.groups.find(
          (group) => group.parentId === parentGroup?.id
        );
        if (
          parentGroup &&
          parentList.findIndex((element) => element.id === parentGroup?.id) ===
            -1
        ) {
          parentList?.push(parentGroup);
        }

        if (
          secondGroup &&
          secondList.findIndex((element) => element.id === secondGroup?.id) ===
            -1
        ) {
          secondList.push(secondGroup);
        }
      });
      parentGroupList.push(parentList);
      secondGroupList.push(secondList);
    });
    _store.update({
      organisationIndexes,
      parentGroupList,
      secondGroupList,
      selectedParentGroupIdList,
      selectedSecondGroupIdList
    });
  };

  const loadCaseDecisionIndexes = (
    caseDecisionIndexes: IOrganisationIndex[]
  ) => {
    const { selectedLanguage } = _store.getState<any>();
    let data = caseDecisionIndexes.map((item) =>
      OrganisationIndexPresentationModel(item, selectedLanguage)
    );
    _store.update({
      caseDecisionIndexes: data,
      selectedIndexes: data
    });
    prepareHierarchyList();
  };
  const getCorrectIndexTranslation = (index: any) => {
    const { selectedLanguage } = store.getState<any>();
    const nameTranslation = index.nameTranslations.find(
      (item) => item.languageId === selectedLanguage?.id
    )?.nameTranslation;
    return nameTranslation ?? index.name;
  };

  const getCorrectGroupTranslation = (data?: any) => {
    const { selectedLanguage } = store.getState<any>();
    if (data) {
      const nameTranslation = data.nameTranslations.find(
        (item) => item.languageId === selectedLanguage?.id
      )?.nameTranslation;
      return nameTranslation ?? data.name;
    } else {
      return "";
    }
  };

  const findParent = (group, groupList, indexHierarchyList) => {
    if (group?.parentId) {
      let g = groupList.find((g) => g.id === group?.parentId);
      if (g) {
        indexHierarchyList.push(getCorrectGroupTranslation(g));
        return findParent(g, groupList, indexHierarchyList);
      }
    } else if (group?.organisationIndexCategoryId) {
      indexHierarchyList.push(group?.categoryName);
    }
    return indexHierarchyList;
  };

  const prepareHierarchyList = () => {
    let indexHierarchyList = state.indexHierarchyList;
    const { caseDecisionIndexes } =
      _store.getState<TCaseDecisionUpdatePresenter>();

    caseDecisionIndexes.forEach((index) => {
      let indexHierarchy: string[] = [];
      let firstGroup = index.groups.find(
        (g) => g.id === index.organisationGroupId
      );
      indexHierarchy.push(getCorrectIndexTranslation(index));
      indexHierarchy.push(getCorrectGroupTranslation(firstGroup));
      indexHierarchy = findParent(firstGroup, index.groups, indexHierarchy);
      indexHierarchyList.push(indexHierarchy);
    });

    _store.update({ indexHierarchyList });
  };
  loader.start("attachmentLoader");
  const loadAttachments = (attachments: IAttachment[]) => {
    const { attachmentTypes } = _store.getState<TCaseDecisionUpdatePresenter>();

    let attachmentList = (attachments || []).map((item) => {
      const attachmentType = attachmentTypes?.find(
        (value) => value.id === item.attachmentTypeId
      );
      return AttachmentPresentationModel(item, attachmentType);
    });
    _store.update({
      attachmentList
    });
    loader.stop("attachmentLoader");
  };

  const createAttachment = async (attachment: any) => {
    loader.start("caseDecisionLoader");
    const {
      uploadedFile,
      anonimisedUploadedFile,
      attachmentTypes,
      contentTypes,
      caseDecision
    } = _store.getState<TCaseDecisionUpdatePresenter>();
    attachment.id =
      attachment.identificationCode === "Y" ? undefined : attachment.id;
    if (uploadedFile) {
      attachment.content = uploadedFile.name;
    }
    const attachmentType = attachmentTypes?.find(
      (value) => value.id === attachment.attachmentTypeId
    );
    attachment.file = uploadedFile;
    let contentType = contentTypes.find(
      (ct) => ct.id === attachmentType?.attachmentContentTypeId
    );
    if (
      contentType?.code === ContentTypes.FILE_CONTENT_TYPE &&
      attachment.file &&
      !attachment.file?.isDummy
    ) {
      if (anonimisedUploadedFile && !anonimisedUploadedFile.isDummy) {
        attachment.anonymised = false;
        let createdAttachment = await _application.container
          .resolve<CreateAttachmentFileInteractor>("createAttachmentFile")
          .execute(attachment, attachment.file, undefined, caseDecision.id);
        createdAttachment.anonymised = true;
        await _application.container
          .resolve<CreateAttachmentFileInteractor>("createAttachmentFile")
          .execute(
            createdAttachment,
            anonimisedUploadedFile,
            undefined,
            caseDecision.id
          );
      } else {
        await _application.container
          .resolve<CreateAttachmentFileInteractor>("createAttachmentFile")
          .execute(attachment, attachment.file, undefined, caseDecision.id);
      }
    } else {
      await _application.container
        .resolve<CreateAttachmentInteractor>("createAttachment")
        .execute(attachment, undefined, caseDecision.id);
    }
    let attachments = await _application.container
      .resolve<GetAttachmentsForCaseDecisionInteractor>(
        "getAttachmentsForCaseDecision"
      )
      .execute(caseDecision.id);
    loadAttachments(attachments);
    _store.update({
      uploadedFile: undefined,
      anonimisedUploadedFile: undefined
    });
    loader.stop("caseDecisionLoader");
    _application.container
      .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
      .execute(translate("editors.project.create_success_message"));
    return true;
  };

  const saveMainAttachmentChange = async () => {
    const {
      mainAttachment,
      caseDecision,
      uploadedFile,
      anonimisedUploadedFile,
      mainAttachmentType,
      contentTypes
    } = _store.getState<TCaseDecisionUpdatePresenter>();
    if (
      mainAttachment.attachmentTypeId ===
        caseDecision.attachment?.attachmentTypeId &&
      mainAttachment.content === caseDecision.attachment?.content &&
      mainAttachment.description === caseDecision.attachment.description
    ) {
      return;
    }
    mainAttachment.file = uploadedFile;
    let contentType = contentTypes.find(
      (ct) => ct.id === mainAttachmentType?.attachmentContentTypeId
    );
    if (
      contentType?.code === ContentTypes.FILE_CONTENT_TYPE &&
      mainAttachment.file &&
      !mainAttachment.file?.isDummy
    ) {
      let createdAttachment = await _application.container
        .resolve<CreateAttachmentFileInteractor>("createAttachmentFile")
        .execute(
          mainAttachment,
          mainAttachment.file,
          undefined,
          caseDecision.id
        );
      if (anonimisedUploadedFile) {
        createdAttachment.anonymised = true;

        await _application.container
          .resolve<CreateAttachmentFileInteractor>("createAttachmentFile")
          .execute(
            createdAttachment,
            anonimisedUploadedFile,
            undefined,
            caseDecision.id
          );
      }
    } else {
      let createdAttachment = await _application.container
        .resolve<CreateAttachmentInteractor>("createAttachment")
        .execute(mainAttachment, undefined, caseDecision.id);
      if (anonimisedUploadedFile && !anonimisedUploadedFile.isDummy) {
        createdAttachment.anonymised = true;
        await _application.container
          .resolve<CreateAttachmentFileInteractor>("createAttachmentFile")
          .execute(
            createdAttachment,
            anonimisedUploadedFile,
            undefined,
            caseDecision.id
          );
      }
    }
  };

  const setFileValue = (file: any) => {
    _store.update({ uploadedFile: file });
  };

  const updateAttachment = async (attachment: any) => {
    const {
      anonimisedUploadedFile,
      attachmentTypes,
      contentTypes,
      caseDecision,
      uploadedFile
    } = _store.getState<TCaseDecisionUpdatePresenter>();
    loader.start("attachmentsLoader");

    const attachmentType = attachmentTypes?.find(
      (value) => value.id === attachment.attachmentTypeId
    );
    attachment.file = uploadedFile;
    let contentType = contentTypes.find(
      (ct) => ct.id === attachmentType?.attachmentContentTypeId
    );
    if (
      contentType?.code === ContentTypes.FILE_CONTENT_TYPE &&
      attachment.file &&
      !attachment.file?.isDummy
    ) {
      let createdAttachment = await _application.container
        .resolve<CreateAttachmentFileInteractor>("createAttachmentFile")
        .execute(attachment, attachment.file, undefined, caseDecision.id);
      if (anonimisedUploadedFile) {
        createdAttachment.anonymised = true;

        await _application.container
          .resolve<CreateAttachmentFileInteractor>("createAttachmentFile")
          .execute(
            createdAttachment,
            anonimisedUploadedFile,
            undefined,
            caseDecision.id
          );
      }
    } else {
      let createdAttachment = await _application.container
        .resolve<CreateAttachmentInteractor>("createAttachment")
        .execute(attachment, undefined, caseDecision.id);
      if (anonimisedUploadedFile && !anonimisedUploadedFile.isDummy) {
        createdAttachment.anonymised = true;
        await _application.container
          .resolve<CreateAttachmentFileInteractor>("createAttachmentFile")
          .execute(
            createdAttachment,
            anonimisedUploadedFile,
            undefined,
            caseDecision.id
          );
      }
    }
    let attachments = await _application.container
      .resolve<GetAttachmentsForCaseDecisionInteractor>(
        "getAttachmentsForCaseDecision"
      )
      .execute(caseDecision.id);
    loadAttachments(attachments);
    _store.update({
      uploadedFile: undefined,
      anonimisedUploadedFile: undefined
    });
    setFileValue(undefined);
    _application.container
      .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
      .execute(translate("editors.project.create_success_message"));

    loader.stop("attachmentsLoader");
    return true;
  };

  const updateMainAttachment = async (attachment: any) => {
    const { uploadedFile } = _store.getState<TCaseDecisionUpdatePresenter>();
    loader.start("attachmentsLoader");
    if (uploadedFile) {
      attachment.content = uploadedFile.name;
    }
    _store.update({ mainAttachment: attachment });
    setFileValue(undefined);
    toggleAttachementModal(false);
    loader.stop("attachmentsLoader");
  };

  const setAttachmentType = async (attachmentType: any) => {
    _store.update({ attachmentType });
  };

  const setMainAttachmentType = async (mainAttachmentType: any) => {
    _store.update({ mainAttachmentType });
  };

  const onUpdate = async (caseDecision: ICaseDecision) => {
    getAttachments(caseDecision);
    getAttachmentTypes(caseDecision);
  };

  const getAttachmentTypes = async (caseDecision: ICaseDecision) => {
    const { contentTypes } = _store.getState<TCaseDecisionUpdatePresenter>();
    let list = await _application.container
      .resolve<GetAllOrganisationAttachmentTypesInteractor>(
        "getAllOrganisationAttachmentTypes"
      )
      .execute(caseDecision.organisationId);
    let attachmentTypes = (list || []).map((item) => {
      const contentType = contentTypes?.find(
        (value) => value.id === item.attachmentContentTypeId
      );
      return AttachmentTypePresentationModel(item, contentType);
    });
    _store.update({ attachmentTypes });
  };

  const getAttachments = async (caseDecision: ICaseDecision) => {
    const { attachmentTypes } = _store.getState<TCaseDecisionUpdatePresenter>();

    let attachments = await _application.container
      .resolve<GetAttachmentsForCaseDecisionInteractor>(
        "getAttachmentsForCaseDecision"
      )
      .execute(caseDecision.id);
    let attachmentList = (attachments || []).map((item) => {
      const attachmentType = attachmentTypes?.find(
        (value) => value.id === item.attachmentTypeId
      );
      return AttachmentPresentationModel(item, attachmentType);
    });
    _store.update({ attachmentList });
  };
  const deleteAttachment = async (attachment: TAttachmentPresentationModel) => {
    const { attachmentList, caseDecision } =
      _store.getState<TCaseDecisionUpdatePresenter>();
    loader.start("caseDecisionLoader");
    var filteredAttachments = attachmentList.filter(
      (item) => item.id === attachment.id
    );
    await _application.container
      .resolve<RemoveCaseDecisionAttachmentInteractor>(
        "removeCaseDecisionAttachment"
      )
      .execute(caseDecision.id, filteredAttachments);
    let attachments = await _application.container
      .resolve<GetAttachmentsForCaseDecisionInteractor>(
        "getAttachmentsForCaseDecision"
      )
      .execute(caseDecision.id);
    loadAttachments(attachments);
    _application.container
      .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
      .execute(translate("editors.project.create_success_message"));
    loader.stop("caseDecisionLoader");
  };
  const showCaseDecisionDetailsPage = (id: string) => {
    return _application.navigator.push({
      pathname: `/case-decisions/${id.toString()}/details`
    });
  };

  const updateCaseDecision = async (item: any) => {
    const { caseDecision, mainAttachment, courtCase } =
      _store.getState<TCaseDecisionUpdatePresenter>();
    loader.start("caseDecisionLoader");
    mainAttachment && (await saveMainAttachmentChange());
    caseDecision.decisionDate = item?.decisionDate?.toDate();
    delete caseDecision.attachment;
    let elementList =
      item.organisationIndexList && item.organisationIndexList.length > 0
        ? item.organisationIndexList.flat(1)
        : [];
    let invalidatedCaseDecisions: IInvalidatedCaseDecision = {
      caseDecisionIdList: item.invalidatedCaseDecisions,
      invalidationReason: item.invalidationReason
    };
    await _application.container
      .resolve<UpdateCaseDecisionInteractor>("updateCaseDecision")
      .execute(caseDecision, elementList, courtCase, invalidatedCaseDecisions);
    _application.container
      .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
      .execute(translate("editors.project_member.create_success_message"));
    loader.stop("caseDecisionLoader");
    showCaseDecisionDetailsPage(caseDecision.id);
  };

  const canEdit = (organisationId) => {
    const { user } = _store.getState<TCaseDecisionUpdatePresenter>();
    if (user) {
      return user.canEdit(organisationId);
    } else {
      return false;
    }
  };

  const toggleAttachementModal = (visible: boolean) => {
    const { mainAttachment } = _store.getState<TCaseDecisionUpdatePresenter>();
    if (visible) {
      setFileValue({
        id: mainAttachment.originalAttachmentId,
        content: mainAttachment.originalAttachmentContent,
        name: mainAttachment.name,
        isDummy: true
      });
      if (
        mainAttachment.anonymised === true ||
        (mainAttachment.anonymised as any) === Constants.YES
      ) {
        setAnonymisedFileValue({
          id: mainAttachment.id,
          content: mainAttachment.content,
          name: mainAttachment.name,
          isDummy: true
        });
      }
    }
    _store.update({ attachmentModalVisible: visible });
  };

  const setAnonymisedFileValue = (file: any) => {
    _store.update({ anonimisedUploadedFile: file });
  };

  const setCaseDecisionTypeCode = async (caseDecisionTypeId: number) => {
    await getCaseDecisionTypeIndexes(caseDecisionTypeId);
    _store.update({
      caseDecisionTypeCode: caseDecisionTypeId
    });
  };

  const setParentGroupId = async (
    selectedParentGroupId: number,
    index: number
  ) => {
    let { selectedParentGroupIdList } =
      _store.getState<TCaseDecisionUpdatePresenter>();
    selectedParentGroupIdList[index] = selectedParentGroupId;
    _store.update({
      selectedParentGroupIdList
    });
  };
  const setSecondGroupId = async (
    selectedSecondGroupId: number,
    index: number
  ) => {
    let { selectedSecondGroupIdList } =
      _store.getState<TCaseDecisionUpdatePresenter>();
    selectedSecondGroupIdList[index] = selectedSecondGroupId;
    _store.update({
      selectedSecondGroupIdList
    });
  };

  const publishCaseDecision = async () => {
    const { caseDecision } = _store.getState<TCaseDecisionUpdatePresenter>();

    await _application.container
      .resolve<PublishCaseDecisionInteractor>("publishCaseDecision")
      .execute(caseDecision.id);
  };
  const archiveCaseDecision = async () => {
    const { caseDecision } = _store.getState<TCaseDecisionUpdatePresenter>();
    await _application.container
      .resolve<ArchiveCaseDecisionInteractor>("archiveCaseDecision")
      .execute(caseDecision);
  };

  const openIndexDataModal = async (
    index: IOrganisationIndex,
    selectedIds: number[]
  ) => {
    let { previousSelectedIds } =
      _store.getState<TCaseDecisionUpdatePresenter>();
    if (
      previousSelectedIds &&
      selectedIds.length < previousSelectedIds?.length
    ) {
      _store.update({
        previousSelectedIds: selectedIds
      });
      return;
    }
    if (index?.indexTypeId) {
      let indexTypeAttributes = await _application.container
        .resolve<GetAllIndexTypeAttributesInteractor>(
          "getAllIndexTypeAttributes"
        )
        .execute(index?.indexTypeId);
      let indexAttributeDataList = await _application.container
        .resolve<GetIndexAttributeDataByIndexInteractor>(
          "getIndexAttributeDataByIndex"
        )
        .execute(index?.id);
      _store.update({
        indexTypeAttributes,
        indexAttributeDataList,
        indexDataModalVisible: true,
        selectedIndexForAttribute: index,
        previousSelectedIds: selectedIds
      });
    }
  };

  const closeIndexDataModal = () => {
    _store.update({
      indexDataModalVisible: false
    });
  };

  const setAttributesData = (
    indexTypeAttribute: IIndexTypeAttribute,
    attributeData: string
  ) => {
    loader.start("caseLawDocumentLoader");
    const { indexAttributeDataList, selectedIndexForAttribute } =
      _store.getState<TCaseDecisionUpdatePresenter>();
    let indexAttributeData = {
      indexId: selectedIndexForAttribute?.id,
      indexTypeAttributeId: indexTypeAttribute?.id,
      attributeData: attributeData,
      valid: true
    };
    let model = IndexAttributeData(indexAttributeData);
    let existingAttributeData = indexAttributeDataList?.find(
      (item) => item.indexTypeAttributeId === model.indexTypeAttributeId
    );
    if (existingAttributeData) {
      let index = indexAttributeDataList?.indexOf(existingAttributeData);
      if (indexAttributeDataList && index !== undefined) {
        indexAttributeDataList[index].attributeData = attributeData;
      }
    } else {
      indexAttributeDataList?.push(model);
    }
    let filteredList = indexAttributeDataList?.filter(
      (d) => d.attributeData && d.attributeData !== ""
    );
    _store.update({
      indexAttributeData: model,
      indexAttributeDataList: filteredList
    });
    loader.stop("caseLawDocumentLoader");
  };

  const saveAttributeData = async () => {
    loader.start("caseLawDocumentLoader");
    const { indexAttributeDataList, indexTypeAttributes } =
      _store.getState<TCaseDecisionUpdatePresenter>();
    let invalidTypes: IIndexTypeAttribute[] = [];
    indexTypeAttributes?.forEach(async (type) => {
      let existingData = indexAttributeDataList?.filter(
        (d) => d.indexTypeAttributeId === type.id && d.attributeData
      );
      if (existingData?.length === 0 && type.required) {
        invalidTypes.push(type);
      }
    });
    if (invalidTypes.length > 0) {
      _application.container
        .resolve<ShowWarningMessageInteractor>("showWarningMessage")
        .execute(translate("editors.case_law_documents.required_data"));
      loader.stop("indexTypeLoader");
      return;
    }
    indexAttributeDataList &&
      (await _application.container
        .resolve<CreateIndexAttributeDataInteractor>("createIndexAttributeData")
        .execute(indexAttributeDataList));
    closeIndexDataModal();
    _application.container
      .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
      .execute(translate("editors.project.create_success_message"));
    _store.update({ indexAttributeDataList: [] });
    loader.stop("caseLawDocumentLoader");
  };

  return {
    ...state,
    store: _store,
    loader,
    application: _application,
    translate,
    loadCaseDecision,
    loadReferenceValues,
    loadCaseDecisionIndexes,
    loadAttachments,
    createAttachment,
    setFileValue,
    updateAttachment,
    updateMainAttachment,
    getAttachments,
    setAttachmentType,
    deleteAttachment,
    onUpdate,
    showCaseDecisionDetailsPage,
    updateCaseDecision,
    canEdit,
    toggleAttachementModal,
    setAnonymisedFileValue,
    publishCaseDecision,
    archiveCaseDecision,
    setParentGroupId,
    setSecondGroupId,
    setCaseDecisionTypeCode,
    setMainAttachmentType,
    setAttributesData,
    openIndexDataModal,
    closeIndexDataModal,
    saveAttributeData
  };
}, defaultState);

export default CaseDecisionUpdatePresenter;
